import React, { useState, useRef, useEffect } from 'react';
import TouchpointCard from './TouchpointCard';
import PlannerTable from './PlannerTable';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import saveThreadToFirestore from '../../utils/saveThreadToFirestore';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { sharedStyles as styles } from '../../styles/alfredStyles';

const containerStyle = {
  backgroundColor: '#f9fafb',
  minHeight: '100vh',
  padding: '40px 20px',
};

const innerStyle = {
  maxWidth: '960px',
  margin: '0 auto',
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  padding: '40px',
  fontFamily: `'Helvetica Neue', sans-serif`,
};

const headerStyle = {
  fontSize: '28px',
  fontWeight: 'bold',
  marginBottom: '30px',
  borderBottom: '4px solid #FFD700',
  display: 'inline-block',
  paddingBottom: '10px',
};

const labelStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: '#333',
  marginBottom: '10px',
  display: 'block',
};

const textareaStyle = {
  width: '95%',
  padding: '16px',
  fontSize: '16px',
  fontFamily: `'Helvetica Neue', sans-serif`,
  lineHeight: '1.6',
  borderRadius: '12px',
  border: '1px solid #ccc',
  backgroundColor: '#f5f5f5',
  height: '100px',
  resize: 'none',
  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
};

const buttonStyle = {
  marginTop: '16px',
  backgroundColor: '#FFD700',
  color: '#000',
  fontSize: '16px',
  fontWeight: '600',
  padding: '12px 24px',
  borderRadius: '12px',
  border: 'none',
  cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
};

const chatBubbleStyle = {
  padding: '12px 16px',
  borderRadius: '16px',
  maxWidth: '80%',
  marginBottom: '12px',
  whiteSpace: 'pre-wrap',
  lineHeight: '1.5',
  fontSize: '15px',
};

const userBubble = {
  ...chatBubbleStyle,
  backgroundColor: '#e0f0ff',
  alignSelf: 'flex-end',
};

const alfredBubble = {
  ...chatBubbleStyle,
  backgroundColor: '#f0f0f0',
  alignSelf: 'flex-start',
};

const chatContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginTop: '24px',
  maxHeight: '400px',
  overflowY: 'auto',
  paddingRight: '8px',
};

const utilityButtonStyle = {
  backgroundColor: '#f6f6f6',
  border: 'none',
  borderRadius: '12px',
  padding: '10px 16px',
  fontWeight: '600',
  fontSize: '15px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  fontFamily: `'Helvetica Neue', sans-serif`,
};

const primaryButtonStyle = {
  backgroundColor: '#FFD700',
  color: '#000',
  fontSize: '16px',
  fontWeight: '600',
  padding: '12px 24px',
  borderRadius: '12px',
  border: 'none',
  cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  fontFamily: `'Helvetica Neue', sans-serif`,
};

const ProspectingOutput = ({
  campaignData: initialCampaignData,
  existingChatHistory = [],
  existingCampaignName = '',
  existingThreadId = null,
  existingFormData = {},
  isReadOnly = false,
  isThread = false,
  scrollToOutputOnMount = true
}) => {
  const generateProspectingThreadName = () => {
    const nameParts = [];

    // Fallback logic for where to find values
    const industry = existingFormData?.industry?.trim() || '';
    const geography = existingFormData?.geography?.trim() || '';
    const persona = existingFormData?.persona?.trim() || '';
    const goal = existingFormData?.goal?.trim() || '';
    const size = existingFormData?.companySize?.trim() || '';

    let name = 'Prospecting Campaign';

    if (persona) name += `: ${persona}`;
    if (geography) name += ` – ${geography}`;
    if (industry) name += ` – ${industry}`;
    if (size) name += ` – ${size}`;
    if (goal) name += ` – Goal: ${goal}`;

    return name;
  };
  const [campaignName, setCampaignName] = useState(existingCampaignName || '');
  const [inputs, setInputs] = useState({});
  const [campaignData, setCampaignData] = useState(initialCampaignData || []);
  const [askText, setAskText] = useState('');
  const [chatHistory, setChatHistory] = useState(existingChatHistory || []);
  useEffect(() => {
    if (existingChatHistory?.length > 0) {
      setChatHistory(existingChatHistory);
    }
  }, [existingChatHistory]);
  const [loadingReply, setLoadingReply] = useState(false);
  const [dots, setDots] = useState('');
  const campaignTopRef = useRef(null);
  const { threadId: urlThreadId } = useParams();
  const initialThreadId = useRef(existingThreadId || urlThreadId || uuidv4()); // Respect loaded thread
  const [threadId, setThreadId] = useState(initialThreadId.current);
  const [isPinned, setIsPinned] = useState(null);
  const isFirstRender = useRef(true);
  const scrollRef = useRef(null);
  const hasScrolled = useRef(false);
  const chatBottomRef = useRef(null);
  const fetchPinnedStatus = async (threadId, setIsPinned) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const docRef = doc(db, 'users', user.uid, 'threads', threadId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      setIsPinned(data?.pinned || false);
    }
  };

  useEffect(() => {
    if (scrollToOutputOnMount && !hasScrolled.current && campaignData && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      hasScrolled.current = true;
    }
  }, [campaignData, scrollToOutputOnMount]);

  useEffect(() => {
    if (!isThread && chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory, isThread]);

  useEffect(() => {
    if (!isThread && campaignData?.length > 0 && isFirstRender.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (campaignTopRef.current) {
            campaignTopRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
            isFirstRender.current = false;
          }
        }, 200);
      });
    }
  }, [campaignData, isThread]);

  useEffect(() => {
    console.log('🧠 Loaded existingFormData:', existingFormData);
  }, [existingFormData]);

  useEffect(() => {
    console.log('✅ Updated inputs:', inputs);
  }, [inputs]);

  useEffect(() => {
    const saveThread = async () => {
      if (!campaignData || campaignData.length === 0) return;
      if (isPinned === null) return;
      if (!inputs || Object.keys(inputs).length === 0) {
        console.warn('⚠️ Skipping save — inputs not ready');
        return;
      }

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      const threadRef = doc(db, 'users', user.uid, 'threads', threadId);
      const existing = await getDoc(threadRef);

      const firstTouch = campaignData[0] || {};
      const derivedMetadata = {
        industry: firstTouch.industry || inputs.industry || '',
        geography: firstTouch.geography || inputs.geography || '',
        persona: firstTouch.persona || inputs.persona || '',
        goal: firstTouch.goal || inputs.goal || '',
        companySize: firstTouch.companySize || inputs.companySize || '',
      };

      const nameParts = [];
      if (derivedMetadata.goal) nameParts.push(derivedMetadata.goal);
      if (derivedMetadata.industry) nameParts.push(derivedMetadata.industry);
      if (derivedMetadata.persona) nameParts.push(derivedMetadata.persona);
      if (derivedMetadata.geography) nameParts.push(derivedMetadata.geography);
      if (derivedMetadata.companySize) nameParts.push(`Size: ${derivedMetadata.companySize}`);

      const autoName = `Prospecting Campaign: ${nameParts.join(' – ') || 'Untitled'}`;
      const finalName =
        existingCampaignName?.trim() ||
        campaignName?.trim() ||
        autoName;

      setCampaignName(finalName);

      await saveThreadToFirestore(user.uid, {
        id: threadId,
        name: finalName,
        pinned: isPinned,
        folders: [],
        type: 'prospecting',
        createdAt: existing.exists() ? existing.data().createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        data: {
          campaign: campaignData,
          chat: chatHistory,
          formInputs: inputs || {},
        },
        metadata: derivedMetadata,
      });
    };

    saveThread();
  }, [campaignData, chatHistory, campaignName, threadId, isPinned, inputs]);


  useEffect(() => {
    let interval;
    if (loadingReply) {
      interval = setInterval(() => {
        setDots((prev) => (prev.length < 3 ? prev + '.' : ''));
      }, 500);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [loadingReply]);

  useEffect(() => {
    if (threadId) {
      fetchPinnedStatus(threadId, setIsPinned);
    }
  }, [threadId]);

  useEffect(() => {
    if (existingFormData && Object.keys(existingFormData).length > 0) {
      setInputs(existingFormData);
    }
  }, [existingFormData]);

  const handleAskAlfred = async () => {
    if (!askText.trim()) return;
    setLoadingReply(true);

    const userMessage = { sender: 'user', text: askText };
    const updatedChat = [...chatHistory, userMessage];
    setChatHistory(updatedChat);
    setAskText('');

    const campaignOutput = campaignData.map((touch) => {
      return `Day ${touch.day} - ${touch.channel} @ ${touch.time}\n${touch.message}\n💡 ${touch.coachingTip || ''}`;
    }).join('\n\n');

    // ✅ MOVE THIS OUTSIDE THE FETCH CALL
    const firstTouch = campaignData[0] || {};

    try {
      const payload = {
        messageHistory: [
          {
            role: 'system',
            content: `Reminder: This campaign is targeting ${firstTouch.persona || 'unknown persona'}s in the ${firstTouch.industry || 'unknown industry'} industry based in ${firstTouch.geography || 'unknown geography'}. The goal is to ${firstTouch.goal || 'unspecified goal'}.`
          },
          ...updatedChat.map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.text
          })),
        ],
        context: {
          industry: firstTouch.industry || '',
          geography: firstTouch.geography || '',
          persona: firstTouch.persona || '',
          goal: firstTouch.goal || '',
          companySize: firstTouch.companySize || '',
          campaignOutput
        }
      };

      console.log('📤 Ask Alfred → Sending payload:', payload);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/campaign/ask-alfred`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      const alfredReply = data.result || data.alfredReply || 'Something went wrong.';
      const newChatHistory = [...updatedChat, { sender: 'alfred', text: alfredReply }];
      setChatHistory(newChatHistory);
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const firstTouch = campaignData[0] || {};
        const updatedMetadata = {
          industry: firstTouch.industry || existingFormData.industry || '',
          geography: firstTouch.geography || existingFormData.geography || '',
          persona: firstTouch.persona || existingFormData.persona || '',
          goal: firstTouch.goal || existingFormData.goal || '',
          companySize: firstTouch.companySize || existingFormData.companySize || '',
        };

        const updatedNameParts = [];
        if (existingFormData.goal) updatedNameParts.push(existingFormData.goal);
        if (existingFormData.industry) updatedNameParts.push(existingFormData.industry);
        if (existingFormData.geography) updatedNameParts.push(existingFormData.geography);
        if (existingFormData.companySize) updatedNameParts.push(`Size: ${existingFormData.companySize}`);
        if (existingFormData.persona) updatedNameParts.push(existingFormData.persona);
        const updatedAutoName = `Prospecting Campaign: ${updatedNameParts.join(' – ') || 'Untitled'}`;

        const finalName = campaignName && campaignName.trim().length > 0
          ? campaignName.trim()
          : updatedAutoName;

        const threadRef = doc(db, 'users', user.uid, 'threads', threadId);
        const existing = await getDoc(threadRef);

        await saveThreadToFirestore(user.uid, {
          id: threadId,
          name: finalName,
          pinned: isPinned,
          folders: [],
          type: 'prospecting',
          createdAt: existing.exists() ? existing.data().createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          data: {
            campaign: campaignData,
            chat: newChatHistory,
            formInputs: inputs || {},
          },
          metadata: updatedMetadata,
        });
      }

    } catch (err) {
      console.error('❌ Ask Alfred failed:', err);
      setChatHistory(prev => [...prev, { sender: 'alfred', text: 'Something went wrong.' }]);
    } finally {
      setLoadingReply(false);
    }
  };


  const [rephrasingIndex, setRephrasingIndex] = useState(null);

  const handleRephrase = async (index) => {
    const { message, channel } = campaignData[index];
    setRephrasingIndex(index); // ✅ highlight the card

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/campaign/rephrase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, channel }),
      });

      const data = await response.json();
      const updated = [...campaignData];
      updated[index].message = data.rephrased;
      setCampaignData(updated);
    } catch (err) {
      console.error('❌ Rephrase failed:', err);
      alert('Could not rephrase message. Please try again.');
    } finally {
      setRephrasingIndex(null); // ✅ clear it once done
    }
  };

  const handleExportCampaignToWord = () => {
    if (!campaignData || campaignData.length === 0) return;

    const paragraphs = [];

    paragraphs.push(new Paragraph({
      children: [
        new TextRun({ text: campaignName || 'Alfred Prospecting Campaign', bold: true, size: 36 }),
      ],
      spacing: { after: 400 },
    }));

    campaignData.forEach(({ day, channel, time, message, coachingTip }) => {
      paragraphs.push(new Paragraph({
        children: [new TextRun({ text: `Day ${day} – ${channel}`, bold: true, size: 28, underline: {} })],
        spacing: { after: 100 },
      }));

      paragraphs.push(new Paragraph({
        children: [new TextRun({ text: `🕐 Recommended Time: ${time}`, italics: true, size: 24 })],
        spacing: { after: 100 },
      }));

      message.split('\n').forEach((line) => {
        paragraphs.push(new Paragraph({
          children: [new TextRun({ text: line, size: 24 })],
          spacing: { after: 100 },
        }));
      });

      if (coachingTip) {
        coachingTip.split('\n').forEach((line, idx) => {
          paragraphs.push(new Paragraph({
            children: [new TextRun({
              text: idx === 0 ? `💡 Why this works: ${line}` : line,
              italics: true,
              color: '6B4C00',
              size: 22
            })],
            spacing: { after: 100 },
          }));
        });

        // Add a little more spacing after tip block
        paragraphs.push(new Paragraph({
          children: [new TextRun('')],
          spacing: { after: 200 },
        }));
      }
    });

    const doc = new Document({
      sections: [{ properties: {}, children: paragraphs }],
    });

    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `${campaignName || 'alfred-prospecting-campaign'}.docx`);
    });
  };

  return (
    <div style={containerStyle}>
      <div style={innerStyle}>
        <div ref={campaignTopRef} style={{ position: 'relative', top: '-80px' }} />
        <h2 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          marginBottom: '12px',
          borderBottom: '4px solid #FFD700',
          display: 'inline-block',
          paddingBottom: '10px',
        }}>
          Prospecting Campaign Plan
        </h2>

        <p style={{
          fontSize: '16px',
          color: '#666',
          marginTop: '4px',
          marginBottom: '24px'
        }}>
          This is a saved prospecting campaign thread. You can continue building or refining your outreach below.
        </p>

        {isThread && (
          <div style={{ ...styles.inner, marginBottom: '32px' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Campaign Goal</label>
              <div style={{ ...styles.input, color: '#6b7280' }}>
                {inputs.goal || '—'}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Industry</label>
              <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.industry || '—'}</div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Geography</label>
              <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.geography || '—'}</div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Target Role / Persona</label>
              <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.persona || '—'}</div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Company Size</label>
              <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.companySize || '—'}</div>
            </div>

            {inputs.contactName && (
              <div style={{ marginBottom: '24px' }}>
                <label style={styles.label}>Contact Name or LinkedIn URL</label>
                <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.contactName}</div>
              </div>
            )}

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Preferred Channels</label>
              <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.channels?.join(', ') || '—'}</div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Tone</label>
              <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.tone || '—'}</div>
            </div>

            {inputs.includePainPoint && (
              <div style={{ marginBottom: '24px' }}>
                <label style={styles.label}>Pain Point</label>
                <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.painPoint || '—'}</div>
              </div>
            )}

            {inputs.mentionProductLine && (
              <div style={{ marginBottom: '24px' }}>
                <label style={styles.label}>Product Line</label>
                <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.productLine || '—'}</div>
              </div>
            )}

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Include Stats</label>
              <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.includeStats ? 'Yes' : 'No'}</div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.label}>Frameworks</label>
              <div style={{ ...styles.input, color: '#6b7280' }}>{inputs.selectedFrameworks?.join(', ') || '—'}</div>
            </div>

          </div>
        )}

        {campaignData.map((touchpoint, index) => (
          <TouchpointCard
            key={index}
            day={touchpoint.day}
            time={touchpoint.time}
            channel={touchpoint.channel}
            message={touchpoint.message}
            coachingTip={touchpoint.coachingTip}
            onRephrase={() => handleRephrase(index)}
            isRephrasing={rephrasingIndex === index} // ✅ add this!
            onEditSave={async (newMessage) => {
              const updated = [...campaignData];
              updated[index].message = newMessage;
              setCampaignData(updated);

              const updatedNameParts = [];
              if (existingFormData.goal) updatedNameParts.push(existingFormData.goal);
              if (existingFormData.industry) updatedNameParts.push(existingFormData.industry);
              if (existingFormData.persona) updatedNameParts.push(existingFormData.persona);
              if (existingFormData.geography) updatedNameParts.push(existingFormData.geography);
              if (existingFormData.companySize) updatedNameParts.push(`Size: ${existingFormData.companySize}`);
              const updatedAutoName = `Prospecting Campaign: ${updatedNameParts.join(' – ') || 'Untitled'}`;

              const finalName = campaignName && campaignName.trim().length > 0
                ? campaignName.trim()
                : updatedAutoName;

              const auth = getAuth();
              const user = auth.currentUser;
              if (user) {
                await saveThreadToFirestore(user.uid, {
                  id: threadId,
                  name: finalName,
                  pinned: isPinned,
                  folders: [],
                  type: 'prospecting',
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  data: {
                    campaign: updated,
                    chat: chatHistory,
                    formInputs: inputs || {},
                  },
                  metadata: {
                    industry: inputs.industry || '',
                    geography: inputs.geography || '',
                    persona: inputs.persona || '',
                    goal: inputs.goal || '',
                    companySize: inputs.companySize || ''
                  }
                });
              }
            }}
          />
        ))}

        <PlannerTable campaign={campaignData} />

        <div style={{ marginTop: '40px' }}>
          <label style={labelStyle}>🏷️ Optional Campaign Name</label>
          <input
            type="text"
            placeholder="e.g., January Mid-Size Roofing Owner Campaign"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            style={{
              width: '96.5%',
              padding: '12px',
              fontSize: '16px',
              borderRadius: '10px',
              border: '1px solid #ccc',
              marginBottom: '15px'
            }}
          />
        </div>

        <button
          onClick={handleExportCampaignToWord}
          style={{
            padding: '12px 24px',
            marginTop: '0px',
            fontSize: '16px',
            backgroundColor: '#FFD700',
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          Export Full Campaign to Word
        </button>

        {/* Ask Alfred chat assistant */}
        <div style={{ marginTop: '50px' }}>          
          <h3 style={labelStyle}>Ask Alfred a Follow-up Question</h3>

          <div style={chatContainerStyle}>
            {chatHistory.map((msg, idx) => (
              <div key={idx} style={msg.sender === 'user' ? userBubble : alfredBubble}>
                <strong>{msg.sender === 'user' ? 'You' : 'Alfred'}:</strong> <br />
                {msg.text}
              </div>
            ))}
            <div ref={chatBottomRef} />
          </div>

          <div style={{ marginTop: '24px' }}>
            {loadingReply ? (
              <div style={{
                ...alfredBubble,
                fontStyle: 'italic',
                marginBottom: '12px',
                maxWidth: '16%',
                color: '#333',
                wordWrap: 'break-word'
              }}>
                <strong>Alfred:</strong><br />
                Alfred is typing{dots}
              </div>
            ) : null}

            <textarea
              id="alfredFollowUp"
              placeholder="e.g., Can you rewrite Step 2 to sound more direct?"
              style={textareaStyle}
              value={askText}
              onChange={(e) => setAskText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAskAlfred();
                }
              }}
            />

            {!loadingReply && (
              <button style={buttonStyle} onClick={handleAskAlfred}>
                Ask Alfred
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProspectingOutput;
